import { useCallback, useRef } from 'react';
import { ApolloClient, InMemoryCache } from '@apollo/client';
import DiceEnvironmentConfig from '../../environment/config';
import { GET_NCOM_AUTH_TOKEN } from '../account/game-play/useGamePlay';

const TWENTY_FIVE_MINUTES = 25 * 60 * 1000;

const ncomAuthClient = new ApolloClient({
    uri: DiceEnvironmentConfig.getApiConfig().ncomGraphUrl,
    cache: new InMemoryCache(),
    name: 'warpzone',
    version: '1.0.0',
    headers: {
        'x-nintendo-graph': 'true',
    },
});

type NcomTokenState = {
    token: string | null;
    expiry: number | null;
};

export const useNcomToken = () => {
    const stateRef = useRef<NcomTokenState>({
        token: null,
        expiry: null,
    });
    const promiseRef = useRef<Promise<string> | null>(null);

    return useCallback(async (): Promise<string> => {
        const now = Date.now();
        if (stateRef.current.token && stateRef.current.expiry && now < stateRef.current.expiry) {
            return stateRef.current.token;
        }
        if (promiseRef.current) {
            return promiseRef.current;
        }
        const promise = (async () => {
            const result = await ncomAuthClient.mutate({ mutation: GET_NCOM_AUTH_TOKEN });
            const token = result.data?.generateGuestTokens?.customerToken;
            if (!token) {
                promiseRef.current = null;
                throw new Error('Failed to fetch Ncom token');
            }
            stateRef.current = {
                token,
                expiry: Date.now() + TWENTY_FIVE_MINUTES,
            };
            promiseRef.current = null;
            return token;
        })();
        promiseRef.current = promise;
        return promise;
    }, []);
};
